import React from 'react';
import { useTranslation } from 'react-i18next';
import Icon from '../../../../components/AppIcon';

const KPIDashboard = ({
  attendanceRate,
  partialCount,
  totalOvertime
}) => {
  const { t } = useTranslation();

  const getAttendanceRateColor = (rate) => {
    if (rate >= 95) return 'text-green-600';
    if (rate >= 85) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getAttendanceRateIcon = (rate) => {
    if (rate >= 95) return 'TrendingUp';
    if (rate >= 85) return 'Minus';
    return 'TrendingDown';
  };

  const formatHoursMinutes = (hours) => {
    const n = Number(hours);
    if (!Number.isFinite(n)) return '0h';

    const sign = n < 0 ? '-' : '';
    const totalMinutes = Math.round(Math.abs(n) * 60);
    const h = Math.floor(totalMinutes / 60);
    const m = totalMinutes % 60;

    if (m === 0) return `${sign}${h}h`;
    return `${sign}${h}h ${m}m`;
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
      {/* Team Attendance Rate */}
      <div className="bg-card border border-border rounded-xl p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-green-500 to-emerald-600 rounded-lg flex items-center justify-center">
              <Icon name="Users" size={20} color="white" />
            </div>
            <div>
              <h3 className="text-sm font-medium text-muted-foreground">{t('Team Attendance Rate')}</h3>
              <p className="text-xs text-muted-foreground">{t('Current period performance')}</p>
            </div>
          </div>
          <div className={`p-2 rounded-lg ${getAttendanceRateColor(attendanceRate)} bg-opacity-10`}>
            <Icon name={getAttendanceRateIcon(attendanceRate)} size={16} className={getAttendanceRateColor(attendanceRate)} />
          </div>
        </div>

        <div className="space-y-2">
          <div className="flex items-end gap-2">
            <span className={`text-3xl font-bold ${getAttendanceRateColor(attendanceRate)}`}>
              {attendanceRate}%
            </span>
            <span className="text-sm text-muted-foreground mb-1">{t('overall')}</span>
          </div>

          {/* Progress Bar */}
          <div className="w-full bg-muted rounded-full h-2">
            <div
              className={`h-2 rounded-full transition-all duration-300 ${
                attendanceRate >= 95 ? 'bg-green-500' :
                attendanceRate >= 85 ? 'bg-yellow-500' : 'bg-red-500'
              }`}
              style={{ width: `${Math.min(attendanceRate, 100)}%` }}
            ></div>
          </div>

          <div className="flex justify-between text-xs text-muted-foreground">
            <span>{t('Target')}: 95%</span>
            <span className={attendanceRate >= 95 ? 'text-green-600' : 'text-muted-foreground'}>
              {attendanceRate >= 95 ? t('Target met') : `${(95 - attendanceRate)?.toFixed(1)}% ${t('to target')}`}
            </span>
          </div>
        </div>
      </div>
      {/* Partial Attendance */}
      <div className="bg-card border border-border rounded-xl p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-orange-500 to-red-600 rounded-lg flex items-center justify-center">
              <Icon name="Clock" size={20} color="white" />
            </div>
            <div>
              <h3 className="text-sm font-medium text-muted-foreground">{t('Partial Attendance')}</h3>
              <p className="text-xs text-muted-foreground">{t('Incomplete work hours')}</p>
            </div>
          </div>
          <div className={`p-2 rounded-lg ${partialCount > 5 ? 'text-red-500 bg-red-50' : 'text-orange-500 bg-orange-50'}`}>
            <Icon name="AlertTriangle" size={16} />
          </div>
        </div>

        <div className="space-y-2">
          <div className="flex items-end gap-2">
            <span className={`text-3xl font-bold ${partialCount > 5 ? 'text-red-500' : 'text-orange-500'}`}>
              {partialCount}
            </span>
            <span className="text-sm text-muted-foreground mb-1">{t('instances')}</span>
          </div>

          <div className="flex items-center gap-2 text-xs">
            <div className={`w-2 h-2 rounded-full ${partialCount > 5 ? 'bg-red-500' : 'bg-orange-500'}`}></div>
            <span className="text-muted-foreground">
              {partialCount === 0 ? t('None') : partialCount <= 3 ? t('Low') : partialCount <= 5 ? t('Moderate') : t('Needs attention')}
            </span>
          </div>
        </div>
      </div>
      {/* Total Overtime Hours */}
      <div className="bg-card border border-border rounded-xl p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg flex items-center justify-center">
              <Icon name="Zap" size={20} color="white" />
            </div>
            <div>
              <h3 className="text-sm font-medium text-muted-foreground">{t('Total Overtime')}</h3>
              <p className="text-xs text-muted-foreground">{t('Current period hours')}</p>
            </div>
          </div>
          <div className={`p-2 rounded-lg ${totalOvertime > 20 ? 'text-purple-500 bg-purple-50' : 'text-blue-500 bg-blue-50'}`}>
            <Icon name="Timer" size={16} />
          </div>
        </div>

        <div className="space-y-2">
          <div className="flex items-end gap-2">
            <span className={`text-3xl font-bold ${totalOvertime > 20 ? 'text-purple-500' : 'text-blue-500'}`}>
              {formatHoursMinutes(totalOvertime)}
            </span>
          </div>

          <div className="flex items-center gap-2 text-xs">
            <div className={`w-2 h-2 rounded-full ${totalOvertime > 20 ? 'bg-purple-500' : 'bg-blue-500'}`}></div>
            <span className="text-muted-foreground">
              {totalOvertime <= 10 ? t('Normal') : totalOvertime <= 20 ? t('Moderate') : t('High')}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default KPIDashboard;
